import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Spin } from 'antd';

import Page404 from './Page404';
import { itemHome, resolveRecordCode } from '../services/recordCodes';
import { longPathFor, parseShortPath } from '../utils/recordCodes';

/**
 * /c/:code/*, /i/:code, /d/:code, /t/:code, /e/:code, /f/:code — a record's
 * short URL whose code this browser has not met yet (KPE-002, KPR-206).
 *
 * A known code never reaches this page: the router's window reads it as the
 * long path already (utils/shortUrlWindow.js). Here the code is resolved
 * (`GET /api/codes/{kind}/{code}`), learned, and the router goes to the
 * long path in place of this one — the address bar keeps the short form,
 * because the window writes the long path back short. Pages beneath a
 * collection, the query and the hash all come along.
 *
 * An ObjectId in place of a code needs no lookup, except an item's, whose
 * collection is read from the item (items move). Every failure — a code that
 * is unknown, malformed, deleted, or not this person's to read — is the one
 * not-found page: the API answers them alike, and so does this.
 */
export default function ShortRecordRoute() {
	const location = useLocation();
	const here = `${location.pathname}${location.search}${location.hash}`;
	const parsed = parseShortPath(here);
	const [state, setState] = useState({ for: null, to: null, missing: false });

	const needsLookup = Boolean(parsed && !parsed.path && !parsed.malformed);

	useEffect(() => {
		if (!needsLookup) return undefined;
		let live = true;
		const ask = parsed.code
			? resolveRecordCode(parsed.kind, parsed.code)
			: (parsed.kind === 'item' && parsed.id ? itemHome(parsed.id) : Promise.reject(new Error('no lookup')));
		ask.then(
			answer => { if (live) setState({ for: here, to: longPathFor(answer, parsed.tail), missing: false }); },
			() => { if (live) setState({ for: here, to: null, missing: true }); },
		);
		return () => { live = false; };
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [here, needsLookup]);

	if (!parsed || parsed.malformed) return <Page404 />;
	if (parsed.path) return <Navigate to={parsed.path} replace />;
	if (state.for === here && state.to) return <Navigate to={state.to} replace />;
	if (state.for === here && state.missing) return <Page404 />;
	return (
		<div className="short-link__resolving">
			<Spin size="large" tip="Opening link…">
				<div className="short-link__resolving-target" />
			</Spin>
		</div>
	);
}
